import type { CooldownClock } from "../combat/CooldownClock.js";

/**
 * Spell-ATB cooldown (S05).
 *
 * Casting a spell triggers a per-caster **spell cooldown** that is
 * separate from the weapon-attack cooldown. Duration scales with the
 * slot the spell was cast from — a Fireball upcast from a 5th-level
 * slot locks the caster out longer than one cast from a 3rd-level slot.
 *
 * Curve (seconds) — data, not engine rule, so it is tune-ready per
 * `specs/combat-spells.md`:
 *
 *   L0 (cantrip) : 2s   (slightly faster than a weapon attack)
 *   L1           : 3s   (roughly a weapon attack)
 *   L2           : 4s
 *   L3           : 5s
 *   L4           : 7s
 *   L5           : 9s
 *   L6           : 12s
 *   L7           : 15s
 *   L8           : 18s
 *   L9           : 22s
 *
 * Cap: levels outside [0, 9] clamp to the nearest bucket.
 */
export const SPELL_COOLDOWN_TABLE: Readonly<Record<number, number>> = {
  0: 2,
  1: 3,
  2: 4,
  3: 5,
  4: 7,
  5: 9,
  6: 12,
  7: 15,
  8: 18,
  9: 22,
};

export const MIN_SPELL_LEVEL = 0;
export const MAX_SPELL_LEVEL = 9;

/**
 * Seconds of cooldown a caster should incur after casting a spell.
 *
 * `spellLevel` is the spell's intrinsic level (from its data). When the
 * player upcasts from a higher slot, pass the slot level too — the
 * cooldown uses the higher of the two, so upcasting a 3rd-level spell
 * from a 5th-level slot locks the caster out for the 5th-level bucket.
 */
export function spellCooldownSeconds(spellLevel: number, slotLevel?: number): number {
  const effective = Math.max(spellLevel, slotLevel ?? spellLevel);
  const clamped = Math.min(MAX_SPELL_LEVEL, Math.max(MIN_SPELL_LEVEL, Math.floor(effective)));
  return SPELL_COOLDOWN_TABLE[clamped] ?? SPELL_COOLDOWN_TABLE[MIN_SPELL_LEVEL]!;
}

/** Canonical timer id for a caster's spell-ATB cooldown. */
export function spellCooldownTimerId(casterInstanceId: string): string {
  return `spell:cd:${casterInstanceId}`;
}

/**
 * Register a caster's spell cooldown on the shared clock. Re-registers
 * if one is already active (spec: casting resets the lockout even if
 * the prior cooldown hadn't expired — the slot system is what prevents
 * spam, not the clock).
 */
export function beginSpellCooldown(
  clock: CooldownClock,
  casterInstanceId: string,
  spellLevel: number,
  slotLevel?: number,
  onExpire?: () => void,
): { readonly timerId: string; readonly duration: number } {
  const timerId = spellCooldownTimerId(casterInstanceId);
  const duration = spellCooldownSeconds(spellLevel, slotLevel);
  clock.register(timerId, duration, () => {
    onExpire?.();
  });
  return { timerId, duration };
}

/** True while the caster is locked out by a spell cooldown. */
export function isSpellCooldownActive(clock: CooldownClock, casterInstanceId: string): boolean {
  return clock.isActive(spellCooldownTimerId(casterInstanceId));
}
